import React, { useState, useEffect, useLayoutEffect } from "react";
import Logo from "../../assets/restaurant/logo.png";
import {
	Menu as MenuIcon,
	X,
	ChevronRight,
	ArrowRight,
	MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const primaryColor = "#43bd98";
const accent = "#D9B8CE";
const CTA_URL =
	"https://creator.applaby.com?onboarding=true&appType=Services&appSubtype=Salon";
const WHATSAPP_URL = "https://wa.me/34608916357";

const getInitialSection = () => {
	const { pathname, hash } = window.location;
	if (hash) return hash.replace("#", "");
	if (
		pathname === "/salones" ||
		pathname === "/salones/" ||
		pathname === "/salones2" ||
		pathname === "/salones2/"
	)
		return "inicio";
	return "inicio";
};

const Menu2 = () => {
	const { t } = useTranslation();
	const [menuOpen, setMenuOpen] = useState(false);
	const [activeSection, setActiveSection] = useState(getInitialSection);

	const menuItems = [
		{ label: t("salon2.menu.home"), id: "inicio" },
		{ label: t("salon2.menu.examples"), id: "ejemplos" },
		{ label: t("salon2.menu.brand"), id: "marca" },
		{ label: t("salon2.menu.howItWorks"), id: "como-funciona" },
		{ label: t("salon2.menu.pricing"), id: "precios" },
	];

	useLayoutEffect(() => {
		const hash = window.location.hash.replace("#", "");
		if (!hash) return;
		const el = document.getElementById(hash);
		if (el) el.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash.replace("#", "");
			if (hash) {
				setActiveSection(hash);
				const el = document.getElementById(hash);
				if (el) el.scrollIntoView({ behavior: "smooth" });
			} else {
				setActiveSection("inicio");
			}
		};
		window.addEventListener("hashchange", handleHashChange);
		return () => window.removeEventListener("hashchange", handleHashChange);
	}, []);

	useEffect(() => {
		const sectionIds = ["ejemplos", "marca", "como-funciona", "precios"];

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setActiveSection(entry.target.id);
					}
				});
			},
			{ rootMargin: "-100px 0px -60% 0px", threshold: 0 }
		);

		sectionIds.forEach((id) => {
			const el = document.getElementById(id);
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		document.body.style.overflow = menuOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [menuOpen]);

	const goTo = (item) => {
		setMenuOpen(false);
		if (item.id === "inicio") {
			window.scrollTo({ top: 0, behavior: "smooth" });
			window.history.replaceState(null, "", "/salones");
			setActiveSection("inicio");
			return;
		}
		window.location.hash = item.id;
	};

	const desktopLinks = menuItems.map((item) => {
		const isActive = activeSection === item.id;
		return (
			<a
				key={item.id}
				href={`/salones#${item.id}`}
				onClick={(e) => {
					e.preventDefault();
					goTo(item);
				}}
				style={{
					textDecoration: "none",
					color: isActive ? accent : "#444",
					fontWeight: isActive ? 600 : 500,
					fontSize: "0.95rem",
					padding: "0.3rem 0",
					transition: "color 0.3s ease",
					cursor: "pointer",
				}}
			>
				{item.label}
			</a>
		);
	});

	return (
		<header
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100%",
				padding: "0.9rem 1.5rem",
				backgroundColor: "#ffffff",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				boxShadow: menuOpen ? "none" : "0 2px 8px rgba(0,0,0,0.05)",
				zIndex: 100,
				fontFamily: "'Poppins', sans-serif",
				boxSizing: "border-box",
			}}
		>
			<a
				href="/salones"
				style={{
					display: "flex",
					alignItems: "center",
					position: "relative",
					zIndex: 2,
				}}
			>
				<img src={Logo} alt="Logo Applaby" style={{ height: "38px" }} />
			</a>

			<button
				type="button"
				onClick={() => setMenuOpen((prev) => !prev)}
				aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
				aria-expanded={menuOpen}
				style={{
					background: menuOpen ? "#f3f4f6" : "none",
					border: menuOpen ? "1px solid #e5e7eb" : "none",
					borderRadius: "999px",
					cursor: "pointer",
					display: "none",
					alignItems: "center",
					justifyContent: "center",
					width: "40px",
					height: "40px",
					margin: "-8px",
					color: "#1a1a1a",
					position: "relative",
					zIndex: 2,
				}}
				className="menu2-toggle"
			>
				{menuOpen ? <X size={20} strokeWidth={1.75} /> : <MenuIcon size={24} />}
			</button>

			<nav
				style={{
					display: "flex",
					alignItems: "center",
					gap: "1.8rem",
				}}
				className="nav2-desktop"
			>
				{desktopLinks}
				<a
					href={CTA_URL}
					style={{
						padding: "0.7rem 1.5rem",
						background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
						color: "#fff",
						borderRadius: "8px",
						fontSize: "0.95rem",
						fontWeight: 600,
						textDecoration: "none",
						whiteSpace: "nowrap",
						boxShadow: "0 4px 12px rgba(67,189,152,0.25)",
					}}
					target="_blank"
					rel="noreferrer"
				>
					{t("salon2.menu.cta")}
				</a>
			</nav>

			<AnimatePresence>
				{menuOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.15, ease: "easeOut" }}
						style={{
							position: "fixed",
							inset: 0,
							zIndex: 1,
							display: "flex",
							flexDirection: "column",
							background: "#fff",
						}}
					>
						<div
							style={{
								pointerEvents: "none",
								position: "absolute",
								top: "-80px",
								right: "-40px",
								width: "280px",
								height: "280px",
								borderRadius: "50%",
								background: "rgba(67,189,152,0.12)",
								filter: "blur(80px)",
							}}
						/>
						<div
							style={{
								pointerEvents: "none",
								position: "absolute",
								bottom: "80px",
								left: "-60px",
								width: "240px",
								height: "240px",
								borderRadius: "50%",
								background: "rgba(67,189,152,0.08)",
								filter: "blur(70px)",
							}}
						/>

						<div style={{ height: "70px", flexShrink: 0 }} />

						<div
							style={{
								position: "relative",
								zIndex: 2,
								display: "flex",
								flexDirection: "column",
								flex: 1,
								minHeight: 0,
								padding: "8px 24px calc(24px + env(safe-area-inset-bottom))",
							}}
						>
							<p
								style={{
									margin: "0 0 28px",
									fontSize: "11px",
									fontWeight: 600,
									letterSpacing: "0.22em",
									textTransform: "uppercase",
									color: primaryColor,
								}}
							>
								{t("salon2.menu.eyebrow")}
							</p>

							<nav
								style={{
									display: "flex",
									flexDirection: "column",
									flex: 1,
									overflowY: "auto",
									minHeight: 0,
								}}
							>
								{menuItems.map((item, i) => {
									const isActive = activeSection === item.id;
									return (
										<a
											key={item.id}
											href={`/salones#${item.id}`}
											onClick={(e) => {
												e.preventDefault();
												goTo(item);
											}}
											style={{
												display: "flex",
												alignItems: "center",
												gap: "16px",
												padding: "16px 0",
												borderBottom: "1px solid #f3f4f6",
												textDecoration: "none",
											}}
										>
											<span
												style={{
													width: "28px",
													fontSize: "11px",
													fontWeight: 500,
													fontVariantNumeric: "tabular-nums",
													color: isActive
														? primaryColor
														: "rgba(67,189,152,0.7)",
												}}
											>
												{String(i + 1).padStart(2, "0")}
											</span>
											<span
												style={{
													flex: 1,
													fontSize: "1.5rem",
													fontWeight: 600,
													letterSpacing: "-0.03em",
													lineHeight: 1,
													color: isActive ? primaryColor : "#111827",
												}}
											>
												{item.label}
											</span>
											<ChevronRight
												size={18}
												color={isActive ? primaryColor : "#d1d5db"}
											/>
										</a>
									);
								})}
							</nav>

							<div style={{ marginTop: "28px", display: "grid", gap: "12px" }}>
								<a
									href={CTA_URL}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										height: "52px",
										borderRadius: "16px",
										background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
										color: "#fff",
										fontSize: "15px",
										fontWeight: 600,
										textDecoration: "none",
										boxShadow: "0 12px 40px rgba(67,189,152,0.28)",
									}}
								>
									{t("salon2.menu.cta")} <ArrowRight size={18} />
								</a>
								<a
									href={WHATSAPP_URL}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										height: "48px",
										borderRadius: "16px",
										border: "1px solid #e5e7eb",
										background: "#fafafa",
										color: "#374151",
										fontSize: "14px",
										fontWeight: 500,
										textDecoration: "none",
									}}
								>
									<MessageCircle size={18} color={primaryColor} />
									{t("salon2.menu.whatsapp")}
								</a>
								<p
									style={{
										margin: "4px 0 0",
										textAlign: "center",
										fontSize: "11px",
										color: "#9ca3af",
									}}
								>
									{t("salon2.menu.trust")}
								</p>
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<style>{`
				@media (max-width: 992px) {
					.nav2-desktop { display: none !important; }
					.menu2-toggle { display: flex !important; }
				}
				[id] {
					scroll-margin-top: 90px;
				}
			`}</style>
		</header>
	);
};

export default Menu2;
